import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  Grid,
  Typography,
  Input,
  InputLabel,
  FormHelperText,
  FormControl,
  IconButton,
  Collapse,
  Paper,
  InputAdornment, Tab,
} from '@mui/material';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTextSlash, faTimes } from '@fortawesome/free-solid-svg-icons';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { CommandPanels, EditorControls, withBaseEditor } from '../../BaseEditor';
import EstimateTablePart from './tp/landestimated';
import Contracts from './payers';
import LandNumberInput from './LandNumberInput';
import OwnerTablePart from './tp/landowner';
import Session from './tp/landsession';

function LandEditor({
  getControl,
  currentUser,
  dataSetter,
  fields,
  data,
  erroredFields,
}) {
  const [tab, setTab] = useState('general');
  const onWarehouseChange = useCallback(
    (e, newRecs) => dataSetter({ landestimatedvalue_set: newRecs }),
    [dataSetter],
  );
  const onOwnerSetChange = useCallback(
    (e, newRecs) => dataSetter({ landplotownerrelation_set: newRecs }),
    [dataSetter],
  );

  const onSessionSetChange = useCallback(
    (e, newRecs) => dataSetter({ landresolutionhistory_set: newRecs }),
    [dataSetter],
  );

  const currentOrg = useMemo(
    () => (currentUser.is_superuser ? data.org && (data.org.id || null) : currentUser.org.id),
    [currentUser.is_superuser, currentUser.org.id, data.org],
  );
  const addrPrefix = useMemo(
    () => (currentUser.org ? currentUser.org.addr_prefix : ''),
    [currentUser.org],
  );
  useEffect(
    () => {
      if (addrPrefix && !data.address) {
        dataSetter({ address: addrPrefix });
      }
    },
    [addrPrefix, data.address, dataSetter],
  );
  const localityFilter = useMemo(
    () => ({
      region__org: currentOrg,
    }),
    [currentOrg],
  );
  const numberErrMessage = useMemo(
    () => {
      if (data.locality && data.locality.id && data.number) {
        if (data.number.slice(0, 10) !== data.locality.koatuu) {
          return `Кадастровий номер повинен починатися з ${data.locality.koatuu}`;
        }
      }
      return null;
    },
    [data.locality, data.number],
  );
  const [addrEditMode, setAddrEditMode] = useState(1);
  const localityAreaFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const streetFilter = useMemo(
    () => ({ locality: data.locality && data.locality.id }),
    [data.locality],
  );
  const houseFilter = useMemo(
    () => ({ street: data.street && data.street.id }),
    [data.street],
  );
  return (
    <TabContext value={tab}>
      <TabList onChange={(e, v) => setTab(v)} textColor="primary" indicatorColor="primary">
        <Tab value="general" label="Загальне" />
        <Tab value="contracts" label="Орендарі" disabled={!data.id} />
        <Tab value="owners" label="Власники" disabled={!data.id} />
        <Tab value="session" label="Рішення сесії із земельної ділянки" disabled={!data.id} />
      </TabList>
      <TabPanel value="general" sx={{ overflow: 'auto' }}>
        <Grid container spacing={2}>
          <Grid item sm={12}>
            <Collapse in={addrEditMode === 1}>
              <FormControl fullWidth margin="dense">
                <InputLabel
                  htmlFor="addr_input"
                  error={!!erroredFields.address}
                >
                  {fields.address.label}
                </InputLabel>
                <Input
                  id="addr_input"
                  value={data.address}
                  onChange={(e) => dataSetter({ address: e.target.value })}
                  error={!!erroredFields.address}
                  // multiline
                  // rows={3}
                  margin="dense"
                  endAdornment={(
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle to address classifier"
                        onClick={() => setAddrEditMode(2)}
                      >
                        <FontAwesomeIcon icon={faEdit} size="xs" />
                      </IconButton>
                    </InputAdornment>
                  )}
                />
                <FormHelperText error>{erroredFields.address}</FormHelperText>
              </FormControl>
            </Collapse>
            <Collapse in={addrEditMode === 2}>
              <Typography variant="body1" color="info.main">{addrPrefix}</Typography>
              <Paper sx={{
                display: 'flex',
                px: 2,
                py: 1,
                gap: 1,
              }}
              >
                {getControl('locality')}
                {getControl('locality_area', { filter: localityAreaFilter })}
                {getControl('street', { filter: streetFilter })}
                {getControl('house', { filter: houseFilter })}
                <IconButton
                  aria-label="toggle to address classifier"
                  onClick={() => setAddrEditMode(1)}
                  edge="end"
                >
                  <FontAwesomeIcon icon={faTextSlash} size="xs" />
                </IconButton>
              </Paper>
            </Collapse>
          </Grid>
          <Grid item xl={3} sm={6} display="flex" alignItems="center">
            <FormControl variant="standard" fullWidth eraror={!!numberErrMessage}>
              <InputLabel htmlFor="LandNumberInput">{fields.number.label}</InputLabel>
              <Input
                value={data.number}
                onChange={(e, v) => dataSetter({ number: v })}
                id="LandNumberInput"
                inputComponent={LandNumberInput}
              />
              {numberErrMessage && (
                <FormHelperText sx={{ color: 'warning.dark' }}>{numberErrMessage}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          <Grid item xl={3} sm={6}>
            {getControl('area', { precision: 5 })}
          </Grid>
          <Grid item xl={3} sm={6}>
            {getControl('ownership')}
          </Grid>
          <Grid item xl={3} sm={6}>
            {getControl('land_category')}
          </Grid>

          <Grid item sm={3}>
            {getControl('specialpurpose', { helpText: data.purpose ? `Раніше було ${data.purpose}` : null })}
          </Grid>
          <Grid item sm={3}>
            {getControl('percent', {
              disabled: data.percent === null,
              value: data.percent === null ? data.specialpurpose_precent : data.percent,
              endAdornment: (
                <InputAdornment position="end">
                  {data.percent === null ? (
                    <IconButton
                      aria-label="Встановити відокремлений відсоток "
                      title="Встановити відокремлений відсоток"
                      onClick={() => dataSetter({ percent: 0 })}
                    >
                      <FontAwesomeIcon icon={faEdit} size="xs" />
                    </IconButton>
                  ) : (
                    <IconButton
                      aria-label="Використовувати значення відсотоку з призначення зімельної ділянки "
                      title="Використовувати значення відсотоку з призначення зімельної ділянки"
                      onClick={() => dataSetter({ percent: null })}
                    >
                      <FontAwesomeIcon icon={faTimes} size="xs" />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            })}
          </Grid>
          <Grid item sm={3}>
            {getControl('locality', { filter: localityFilter })}
          </Grid>
          <Grid item sm={3}>
            {getControl('land_certificate')}
          </Grid>
          <Grid item sm={12}>
            <EditorControls.TextInput
              label={fields.notes.label}
              readOnly={fields.notes.read_only}
              required={fields.notes.required}
              value={data.notes}
              onChange={(e, v) => dataSetter({ notes: v })}
            />

          </Grid>
          {/* <Grid item sm={12}> */}
          {/*  {getControl('is_easement')} */}
          {/* </Grid> */}
          {currentUser.is_superuser && (
            <Grid item sm={12}>
              {getControl('org', {
                backend: '/api/catalog/orgs/',
              })}
            </Grid>
          )}
          <Grid item sm={12}>
            <Typography
              variant="h4"
              color="primary"
            >
              {fields.landestimatedvalue_set.label}
            </Typography>
            <EstimateTablePart
              data={data.landestimatedvalue_set}
              options={fields.landestimatedvalue_set}
              onChange={onWarehouseChange}
              errors={erroredFields.landestimatedvalue_set}
            />
          </Grid>
        </Grid>
      </TabPanel>
      <TabPanel value="contracts" sx={{ overflow: 'auto' }}>
        <Contracts landId={data.id} />
      </TabPanel>
      <TabPanel value="session" sx={{ overflow: 'auto' }}>
        <Session
          landId={data.id}
          data={data.landresolutionhistory_set}
          options={fields.landresolutionhistory_set}
          onChange={onSessionSetChange}
          errors={erroredFields.landresolutionhistory_set}
          currentOrg={currentOrg}
        />
      </TabPanel>
      <TabPanel value="owners" sx={{ overflow: 'auto' }}>
        <OwnerTablePart
          data={data.landplotownerrelation_set}
          options={fields.landplotownerrelation_set}
          onChange={onOwnerSetChange}
          errors={erroredFields.landplotownerrelation_set}
          currentOrg={currentOrg}
        />
      </TabPanel>
    </TabContext>
  );
}

LandEditor.propTypes = {
  getControl: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    landestimatedvalue_set: PropTypes.arrayOf(PropTypes.shape({})),
    landplotownerrelation_set: PropTypes.arrayOf(PropTypes.shape({})),
    landresolutionhistory_set: PropTypes.arrayOf(PropTypes.shape({})),
    number: PropTypes.string,
    org: PropTypes.shape({
      id: PropTypes.number,
    }),
    locality: PropTypes.shape({
      id: PropTypes.number,
      koatuu: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    street: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    land_certificate: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    house: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
    }),
    address: PropTypes.string,
    purpose: PropTypes.string,
    percent: PropTypes.number,
    specialpurpose_precent: PropTypes.number,
    notes: PropTypes.string,
  }).isRequired,
  dataSetter: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    org: PropTypes.shape({
      id: PropTypes.number,
      repr: PropTypes.string,
      addr_prefix: PropTypes.string,
    }),
    is_superuser: PropTypes.bool,
  }).isRequired,
  fields: PropTypes.shape({
    landestimatedvalue_set: PropTypes.shape({
      label: PropTypes.string,
    }),
    landplotownerrelation_set: PropTypes.shape({
      label: PropTypes.string,
    }),
    landresolutionhistory_set: PropTypes.shape({
      label: PropTypes.string,
    }),
    number: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      max_length: PropTypes.number,
    }),
    address: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      max_length: PropTypes.number,
    }),
    locality: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    locality_area: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    street: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    house: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
      resource: PropTypes.string,
    }),
    notes: PropTypes.shape({
      label: PropTypes.string,
      required: PropTypes.bool,
      read_only: PropTypes.bool,
    }),
  }).isRequired,
  erroredFields: PropTypes.shape({
    address: PropTypes.arrayOf(PropTypes.string),
    landestimatedvalue_set: PropTypes.arrayOf(PropTypes.shape),
    landplotownerrelation_set: PropTypes.arrayOf(PropTypes.shape),
    landresolutionhistory_set: PropTypes.arrayOf(PropTypes.shape),
  }).isRequired,
};

export default withBaseEditor(LandEditor, '/api/realty/lands/', CommandPanels.catCommandPanel);
